import React from 'react';

const HTB = ({ data, language }) => {
  const htb = data.htb || { machines: [], certifications: [] };
  
  const getDifficultyColor = (difficulty) => {
    switch (difficulty) {
      case 'insane': return '#ff0000';
      case 'hard': return '#ff6b00';
      case 'medium': return '#ffb800';
      case 'easy': return '#00ff00';
      default: return '#b06bff';
    }
  };

  const getDifficultyIcon = (difficulty) => {
    switch (difficulty) {
      case 'insane': return '💀';
      case 'hard': return '🔥';
      case 'medium': return '⚡';
      case 'easy': return '✅';
      default: return '🎯';
    }
  };

  const texts = {
    es: {
      title: '🎯 HackTheBox - Logros',
      subtitle: 'Máquinas completadas y certificaciones de labs',
      machines: 'Máquinas Completadas',
      certs: 'Certificaciones de Labs',
      viewBadge: 'Ver Badge',
      downloadCert: 'Descargar PDF',
      profile: 'Ver Perfil HTB',
      stats: 'Estadísticas',
      insane: 'Insane',
      hard: 'Hard'
    },
    en: {
      title: '🎯 HackTheBox - Achievements',
      subtitle: 'Completed machines and lab certifications',
      machines: 'Completed Machines',
      certs: 'Lab Certifications',
      viewBadge: 'View Badge',
      downloadCert: 'Download PDF',
      profile: 'View HTB Profile',
      stats: 'Statistics',
      insane: 'Insane',
      hard: 'Hard'
    }
  };

  const t = texts[language] || texts.es;
  
  const insaneCount = htb.machines.filter(m => m.difficulty === 'insane').length;
  const hardCount = htb.machines.filter(m => m.difficulty === 'hard').length;

  return (
    <div className="section-content htb-section">
      <h2 className="section-title">{t.title}</h2>
      <p style={{ color: 'var(--text-secondary)', marginBottom: '20px', opacity: 0.8 }}>
        {t.subtitle}
      </p>

      {/* Stats */}
      <div className="htb-stats" style={{
        display: 'flex',
        gap: '20px',
        marginBottom: '30px',
        flexWrap: 'wrap'
      }}>
        <div className="stat-box" style={{
          background: 'rgba(255, 0, 0, 0.1)',
          border: '1px solid #ff0000',
          padding: '15px 25px',
          borderRadius: '8px',
          textAlign: 'center'
        }}>
          <div style={{ fontSize: '28px', fontWeight: 'bold', color: '#ff0000' }}>{insaneCount}</div>
          <div style={{ color: '#ff0000', fontSize: '12px' }}>💀 {t.insane}</div>
        </div>
        <div className="stat-box" style={{
          background: 'rgba(255, 107, 0, 0.1)',
          border: '1px solid #ff6b00',
          padding: '15px 25px',
          borderRadius: '8px',
          textAlign: 'center'
        }}>
          <div style={{ fontSize: '28px', fontWeight: 'bold', color: '#ff6b00' }}>{hardCount}</div>
          <div style={{ color: '#ff6b00', fontSize: '12px' }}>🔥 {t.hard}</div>
        </div>
        <a 
          href={data.htbProfile} 
          target="_blank" 
          rel="noopener noreferrer"
          className="htb-profile-btn"
          style={{
            background: 'linear-gradient(135deg, #9fef00 0%, #a4ff00 100%)',
            color: '#000',
            padding: '15px 25px',
            borderRadius: '8px',
            textDecoration: 'none',
            fontWeight: 'bold',
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            transition: 'transform 0.2s, box-shadow 0.2s'
          }}
        >
          <span>👤</span> {t.profile}
        </a>
      </div>

      {/* Machines */}
      <h3 style={{ color: 'var(--steel-bright)', marginBottom: '15px', borderBottom: '1px solid var(--border-color)', paddingBottom: '10px' }}>
        {t.machines}
      </h3>
      <div className="htb-machines" style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
        gap: '15px',
        marginBottom: '30px'
      }}>
        {htb.machines.map((machine) => (
          <div 
            key={machine.id}
            className="htb-machine-card"
            style={{
              background: `linear-gradient(135deg, rgba(${machine.difficulty === 'insane' ? '255,0,0' : '255,107,0'}, 0.05) 0%, rgba(0,0,0,0.3) 100%)`,
              border: `1px solid ${getDifficultyColor(machine.difficulty)}`,
              borderRadius: '8px',
              padding: '15px',
              transition: 'transform 0.2s, box-shadow 0.2s'
            }}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
              <span style={{ fontSize: '20px' }}>{getDifficultyIcon(machine.difficulty)}</span>
              <span style={{ 
                color: getDifficultyColor(machine.difficulty), 
                fontSize: '11px', 
                fontWeight: 'bold',
                textTransform: 'uppercase',
                background: `${getDifficultyColor(machine.difficulty)}20`,
                padding: '3px 8px',
                borderRadius: '4px'
              }}>
                {machine.difficulty}
              </span>
            </div>
            <div style={{ color: 'var(--text-primary)', fontWeight: '600', marginBottom: '5px' }}>
              {machine.name}
            </div>
            <a 
              href={machine.url}
              target="_blank"
              rel="noopener noreferrer"
              style={{
                color: getDifficultyColor(machine.difficulty),
                fontSize: '12px',
                textDecoration: 'none',
                display: 'flex',
                alignItems: 'center',
                gap: '5px'
              }}
            >
              🏆 {t.viewBadge} →
            </a>
          </div>
        ))}
      </div>

      {/* Lab Certifications */}
      <h3 style={{ color: 'var(--steel-bright)', marginBottom: '15px', borderBottom: '1px solid var(--border-color)', paddingBottom: '10px' }}>
        {t.certs}
      </h3>
      <div className="htb-certs" style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fill, minmax(250px, 1fr))',
        gap: '15px'
      }}>
        {htb.certifications.map((cert, index) => (
          <div 
            key={index}
            className="htb-cert-card"
            style={{
              background: 'linear-gradient(135deg, rgba(159, 239, 0, 0.1) 0%, rgba(0,0,0,0.3) 100%)',
              border: '1px solid #9fef00',
              borderRadius: '8px',
              padding: '20px',
              textAlign: 'center'
            }}
          >
            <div style={{ fontSize: '40px', marginBottom: '10px' }}>📜</div>
            <div style={{ color: '#9fef00', fontWeight: 'bold', fontSize: '18px', marginBottom: '15px' }}>
              {cert.name}
            </div>
            <a 
              href={cert.pdfUrl}
              target="_blank"
              rel="noopener noreferrer"
              style={{
                background: 'linear-gradient(135deg, #9fef00 0%, #7bc700 100%)',
                color: '#000',
                padding: '8px 16px',
                borderRadius: '5px',
                textDecoration: 'none',
                fontWeight: 'bold',
                fontSize: '12px',
                display: 'inline-block'
              }}
            >
              📥 {t.downloadCert}
            </a>
          </div>
        ))}
      </div>

      <style>{`
        .htb-machine-card:hover {
          transform: translateY(-3px);
          box-shadow: 0 5px 20px rgba(176, 107, 255, 0.2);
        }
        .htb-cert-card:hover {
          transform: translateY(-3px);
          box-shadow: 0 5px 20px rgba(159, 239, 0, 0.3);
        }
        .htb-profile-btn:hover {
          transform: scale(1.05);
          box-shadow: 0 0 20px rgba(159, 239, 0, 0.5);
        }
      `}</style>
    </div>
  );
};

export default HTB;
